/**
 * A quick, simple drawing of a plan (straight edges, no smoothing) for previews where the real
 * renderer isn't available, such as the History panel. B6 can pass its own preview instead.
 */
import type { Area, PlanDoc, Pt } from '../../../shared/types';

const FILL: Record<Area['kind'], string> = {
  habitat: 'var(--map-habitat)',
  aviary: 'var(--map-aviary)',
  aquarium: 'var(--map-aquarium)',
  lake: 'var(--map-water)',
  pool: 'var(--map-water)',
  plaza: 'var(--map-plaza)',
  backOfHouse: 'var(--map-boh)',
};

// Water and pools go on top of habitats.
const ORDER: Area['kind'][] = ['habitat', 'aviary', 'aquarium', 'plaza', 'backOfHouse', 'lake', 'pool'];

/** An SVG sketch of `doc`, scaled to fit its box. */
export function PlanSketch({ doc, className }: { doc: PlanDoc; className?: string }) {
  const pts = (ids: string[]) =>
    ids
      .map((id) => doc.verts[id])
      .filter((v) => v !== undefined)
      .map((v) => `${v.x},${v.y}`)
      .join(' ');
  const areas = Object.entries(doc.areas).sort((a, b) => ORDER.indexOf(a[1].kind) - ORDER.indexOf(b[1].kind));
  const line = (p: Pt[]) => p.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <svg className={className ?? 'sketch'} viewBox={`0 0 ${doc.size} ${doc.size}`} role="img" aria-label="Plan preview">
      <rect width={doc.size} height={doc.size} fill="var(--map-ground)" />
      {doc.plot && <polygon points={pts(doc.plot.verts)} fill="var(--map-plot)" stroke="var(--map-edge)" strokeWidth={doc.size / 200} />}
      {areas.map(([id, a]) => (
        <polygon key={id} points={pts(a.verts)} fill={FILL[a.kind]} stroke="var(--map-edge)" strokeWidth={doc.size / 400} />
      ))}
      {Object.entries(doc.rivers).map(([id, r]) => (
        <polyline key={id} points={line(r.pts)} fill="none" stroke="var(--map-water)" strokeWidth={r.w} strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {Object.entries(doc.roads).map(([id, r]) => {
        const nodes = r.nodes.map((n) => doc.nodes[n]).filter((n) => n !== undefined);
        const w = nodes.reduce((s, n) => s + n.w, 0) / Math.max(1, nodes.length);
        const p = nodes.map((n): Pt => [n.x, n.y]);
        return r.closed ? (
          <polygon key={id} points={line(p)} fill="none" stroke="var(--map-road)" strokeWidth={w} strokeLinejoin="round" />
        ) : (
          <polyline key={id} points={line(p)} fill="none" stroke="var(--map-road)" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
        );
      })}
      {Object.entries(doc.icons).map(([id, i]) => (
        <circle key={id} cx={i.x} cy={i.y} r={doc.size / 90} fill="var(--accent)" />
      ))}
    </svg>
  );
}

/** "12 habitats · 5 roads": a one-line summary of what's in a plan. */
export function planSummary(doc: PlanDoc): string {
  const areas = Object.values(doc.areas);
  const count = (k: Area['kind']) => areas.filter((a) => a.kind === k).length;
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => n && parts.push(`${n} ${n === 1 ? one : many}`);
  add(count('habitat'), 'habitat', 'habitats');
  add(count('aviary'), 'aviary', 'aviaries');
  add(count('aquarium'), 'aquarium', 'aquariums');
  add(Object.keys(doc.roads).length, 'path', 'paths');
  add(count('lake') + Object.keys(doc.rivers).length, 'lake or river', 'lakes and rivers');
  add(Object.keys(doc.icons).length, 'shop or service', 'shops and services');
  return parts.length ? parts.join(' · ') : 'Empty land';
}
